import styled, { css } from "styled-components";
import { activeColor, borderRadius, buttonBorderRadius, color, hoverColor, transition, typeStyle, units, variant } from "@m/alchemy-theme";
import { lighten, rgba } from "polished";
const priorityVariants = variant("priority", {
    primary: css `
    background-color: ${color("base")};
    color: ${color("inverse")};
  `,
    secondary: css `
    background-color: transparent;
    ${(props) => css `
      ${props.activeColorScheme === "alchemyBrand"
        ? css `
            border-color: ${props.palette === "interactiveDestructive" ? color("base") : color("lineContrast")};
            color: ${color("inverse")};
          `
        : css `
            border-color: ${color("lineContrast")};
            color: ${color("base")};
          `}
    `}
  `,
    tertiary: css `
    border-color: transparent;
    background-color: transparent;
    color: ${({ activeColorScheme }) => (activeColorScheme === "alchemyBrand" ? color("inverse") : color("base"))};
  `,
    global: css `
    background-color: ${({ activeColorScheme }) => activeColorScheme === "alchemyBrand" ? color("inverse") : color("hint")};
    color: ${color("contrast")};
  `,
    success: css `
    background-color: ${color("base", { palette: "interactiveAction" })};
    color: ${color("inverse")};
  `
});
const activeVariants = variant("priority", {
    primary: css `
    background-color: ${activeColor("base")};
  `,
    secondary: css `
    background-color: ${color("hint")};
  `,
    tertiary: css `
    background-color: ${color("hint")};
  `,
    global: css `
    background-color: ${color("hint")};
  `,
    success: css `
    background-color: ${color("hint", { palette: "interactiveAction" })};
  `
});
const hoverVariants = variant("priority", {
    primary: css `
    background-color: ${hoverColor("base")};
  `,
    secondary: css `
    ${(props) => css `
      ${props.activeColorScheme === "alchemyBrand"
        ? css `
            background-color: ${rgba(color("highlight")(props), 0.35)};
            border-color: ${rgba(color("inverse")(props), 0.2)};
          `
        : css `
            background-color: ${color("hint")};
            border-color: ${color("highlight")};
            color: ${props.palette === "interactiveConstructive" ? color("typeLink") : color("contrast")};
          `}
    `}
  `,
    tertiary: css `
    ${(props) => css `
      ${props.activeColorScheme === "alchemyBrand"
        ? css `
            background-color: ${rgba(color("highlight")(props), 0.35)};
            border-color: ${rgba(color("inverse")(props), 0.2)};
          `
        : css `
            background-color: ${color("hint")};
            border-color: ${color("highlight")};
            color: ${props.palette === "interactiveConstructive" ? color("typeLink") : color("contrast")};
          `}
    `}
  `,
    global: css `
    border-color: ${color("highlight")};
  `,
    success: css `
    background-color: ${color("contrast", { palette: "interactiveAction" })};
  `
});
const keyboardFocusVariants = variant("priority", {
    primary: css `
    ${(props) => css `
      ${props.activeColorScheme === "alchemyBrand"
        ? css `
            background-color: ${hoverColor("base")};
            box-shadow:
              0px 0px 0px 2px ${color("container")},
              0px 0px 0px 4px ${rgba(color("inverse")(props), 0.6)};
          `
        : css `
            background-color: ${color("base")};
            box-shadow:
              0px 0px 0px 2px ${color("inverse")},
              0px 0px 0px 4px ${color("base")};
          `}
    `}
  `,
    secondary: css `
    ${(props) => css `
      ${props.activeColorScheme === "alchemyBrand"
        ? css `
            background-color: ${rgba(color("highlight")(props), 0.35)};
            box-shadow: 0px 0px 0px 2px ${rgba(color("inverse")(props), 0.6)};
          `
        : css `
            background-color: ${color("hint")};
            border-color: ${color("base")};
            box-shadow: 0px 0px 0px 1px ${color("base")};
          `}
    `}
  `,
    tertiary: css `
    ${(props) => css `
      ${props.activeColorScheme === "alchemyBrand"
        ? css `
            background-color: ${rgba(color("highlight")(props), 0.35)};
            box-shadow: 0px 0px 0px 2px ${rgba(color("inverse")(props), 0.6)};
          `
        : css `
            background-color: ${color("hint")};
            border-color: ${color("base")};
            box-shadow: 0px 0px 0px 1px ${color("base")};
          `}
    `}
  `,
    global: css `
    ${(props) => css `
      ${props.activeColorScheme === "alchemyBrand"
        ? css `
            background-color: ${color("inverse")};
            box-shadow:
              0px 0px 0px 2px ${color("container")},
              0px 0px 0px 4px ${rgba(color("inverse")(props), 0.6)};
          `
        : css `
            background-color: ${color("hint")};
            border-color: ${color("base")};
            box-shadow: 0px 0px 0px 1px ${color("base")};
          `}
    `}
  `,
    success: css `
    ${(props) => css `
      ${props.activeColorScheme === "alchemyBrand"
        ? css `
            background-color: ${color("contrast", { palette: "interactiveAction" })};
            box-shadow:
              0px 0px 0px 2px ${color("container")},
              0px 0px 0px 4px ${rgba(color("inverse")(props), 0.6)};
          `
        : css `
            background-color: ${color("contrast", { palette: "interactiveAction" })};
            box-shadow:
              0px 0px 0px 2px ${color("inverse")},
              0px 0px 0px 4px ${color("contrast", { palette: "interactiveAction" })};
          `}
    `}
  `
});
const disabledVariants = variant("priority", {
    primary: css `
    ${(props) => !props.isLoading &&
        css `
        background-color: ${props.activeColorScheme === "alchemyBrand"
            ? color("base")
            : color("base", { palette: "structural" })};
      `};
  `,
    secondary: css `
    color: ${color("base", { palette: "structural" })};
  `,
    tertiary: css `
    color: ${color("base", { palette: "structural" })};
  `,
    global: css `
    ${(props) => !props.isLoading &&
        css `
        background-color: ${props.activeColorScheme === "alchemyBrand"
            ? lighten(0.35, color("base")(props))
            : lighten(0.35, color("base", { palette: "structural" })(props))};
        color: ${lighten(0.3, color("typeBody")(props))};
      `};
  `,
    success: css `
    ${(props) => !props.isLoading &&
        css `
        background-color: ${props.activeColorScheme === "alchemyBrand"
            ? color("base")
            : color("base", { palette: "structural" })};
      `};
  `
});
const sizeVariants = variant("size", {
    extraSmall: css `
    border-radius: ${buttonBorderRadius("extraSmall")};
    min-height: ${units(2.5)};
    padding: 0 ${units(1.5)};
    ${typeStyle("actionS")};
  `,
    small: css `
    min-height: 26px;
    border-radius: ${buttonBorderRadius("small")};
    padding: ${units(0.75)} ${units(1.75)};
  `,
    medium: css `
    border-radius: ${buttonBorderRadius("medium")};
    min-height: 34px;
    padding: ${units(1.25)} ${units(2)};
  `,
    large: css `
    border-radius: ${buttonBorderRadius("large")};
    min-height: 44px;
    padding: ${units(1.25)} ${units(2.5)};
  `
});
export const Button = styled.button `
  ${typeStyle("actionM")};
  appearance: none;
  border-radius: ${borderRadius()};
  border: 1px solid transparent;
  cursor: ${({ isLoading, disabled }) => {
    if (isLoading) {
        return "wait";
    }
    if (disabled) {
        return "not-allowed";
    }
    return "pointer";
}};
  display: ${({ displayStyle }) => displayStyle};
  line-height: 1;
  margin: 0;
  position: relative;
  text-align: center;
  text-decoration: none;
  transition: ${transition()};
  transition-property: background-color, border-color, color;
  white-space: nowrap;

  [data-whatinput="touch"] & {
    user-select: none;
  }

  &[disabled] {
    ${(props) => !props.isLoading && `opacity: 0.35;`};
  }

  ${sizeVariants};
  ${priorityVariants};

  &:not([disabled]):active {
    ${activeVariants};
  }

  &:not([disabled]):hover {
    ${hoverVariants};
  }

  [data-whatinput="keyboard"] &&:not([disabled]):focus {
    outline: none;
    ${keyboardFocusVariants};
  }

  &[disabled] {
    ${disabledVariants};
  }
`;
Button.defaultProps = {
    size: "medium"
};
export const Content = styled.div `
  ${({ isLoading }) => isLoading && "opacity: 0;"};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const iconVariants = variant("size", {
    extraSmall: css `
    margin-${(props) => (props.iconPosition === "left" ? "right" : "left")}: ${units(0.5)};
    width: ${units(1.5)};
    height: ${units(1.5)};

    svg {
      width: ${units(1.5)};
      height: ${units(1.5)};
    }
  `,
    small: css `
    margin-${(props) => (props.iconPosition === "left" ? "right" : "left")}: ${units(0.5)};
    width: ${units(2)};
    height: ${units(2)};

    svg {
      width: ${units(2)};
      height: ${units(2)};
    }
  `,
    medium: css `
    margin-${(props) => (props.iconPosition === "left" ? "right" : "left")}: ${units(0.75)};
    width: ${units(2)};
    height: ${units(2)};

    svg {
      width: ${units(2)};
      height: ${units(2)};
    }
  `,
    large: css `
    margin-${(props) => (props.iconPosition === "left" ? "right" : "left")}: ${units(1)};
    width: ${units(2)};
    height: ${units(2)};

    svg {
      width: ${units(2)};
      height: ${units(2)};
    }
  `
});
export const ButtonIconWrapper = styled.span `
  display: flex;
  align-items: center;

  ${iconVariants}
`;
